// ReviewItemCard — a single "Needs Review" card on the Agents Kanban.
//
// Per the hybrid review model: simple items (generic tasks, per-call draft
// batches) get inline Approve / Reject right on the card; the richer kinds
// (monthly-narrative variants, composer-draft workbenches) expose a "Review →"
// affordance that opens <ReviewItemPanel> with the full review UI. All cards
// share the Kanban task-card look (agent colour stripe, eyebrow, summary).

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2, Bot, ThumbsUp, XCircle } from 'lucide-react';
import { useAgent } from '@/contexts/AgentContext';
import { Button } from '@/components/ui';
import { api } from '@/lib/api';
import { submitReviewDecisions } from '@/lib/agent-reviewables';
import { useToast } from '@/lib/useToast';
import { cn } from '@/lib/utils';
import type { ReviewableKind, ReviewDecision } from '@septima/contract';
import type { CallDraftsReviewItem, ReviewQueueItem } from '@/lib/use-review-queue';
import { agentColorForCode, agentRoleForCode } from '../agent-kanban-meta';
import RejectionDialog from '../RejectionDialog';

interface ReviewItemCardProps {
    item: ReviewQueueItem;
    /** Open the focused review drawer for this item. */
    onOpenPanel: (item: ReviewQueueItem) => void;
    /** Ring highlight when arrived at via a `?focus=` deep link. */
    highlighted?: boolean;
}

// Drop a trailing "· Run <AGENT> now" affordance clause from a meta label,
// leaving just the persona/area before the middle dot.
function stripRunSuffix(label: string): string {
    const parts = label.split(' · ');
    if (parts.length > 1 && /run .* now/i.test(parts[parts.length - 1])) {
        return parts.slice(0, -1).join(' · ');
    }
    return label;
}

export default function ReviewItemCard({ item, onOpenPanel, highlighted }: ReviewItemCardProps) {
    const agentColor = agentColorForCode(item.agent);

    return (
        <div
            id={`review-card-${item.id}`}
            style={{ ['--agent' as string]: agentColor }}
            className={cn(
                'rounded-lg border bg-[rgb(var(--color-surface-primary))] transition-all duration-200 text-left scroll-mt-4',
                highlighted
                    ? 'border-[rgb(var(--color-accent))] ring-2 ring-[rgb(var(--color-accent)/0.4)]'
                    : 'border-[rgb(var(--color-divider)/0.5)] hover:border-[rgb(var(--color-divider))] hover:shadow-md',
            )}
        >
            <div className="h-1 rounded-t-lg bg-[rgb(var(--agent))]" />
            <div className="px-3.5 pt-2.5 pb-3">
                <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-1.5">
                        <Bot className="w-3 h-3 text-[rgb(var(--agent))]" />
                        <span
                            className="text-annotation font-bold tracking-wider uppercase text-[rgb(var(--agent))]"
                            title={agentRoleForCode(item.agent)}
                        >
                            {item.agent}
                        </span>
                    </div>
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-mono font-bold bg-[rgb(var(--color-rag-amber)/0.12)] text-[rgb(var(--color-rag-amber))]">
                        {item.pendingCount}
                    </span>
                </div>

                <p className="text-body-medium text-[rgb(var(--color-text-primary))] leading-snug line-clamp-1">
                    {item.primaryLabel}
                </p>
                <p className="text-annotation text-[rgb(var(--color-text-secondary))] mb-2.5 line-clamp-2">
                    {stripRunSuffix(item.secondaryLabel)}
                </p>

                <CardActions item={item} onOpenPanel={onOpenPanel} />
            </div>
        </div>
    );
}

function CardActions({
    item,
    onOpenPanel,
}: {
    item: ReviewQueueItem;
    onOpenPanel: (item: ReviewQueueItem) => void;
}) {
    switch (item.kind) {
        case 'task':
            return <TaskActions item={item} onOpenPanel={onOpenPanel} />;
        case 'call_drafts':
            return <CallDraftsActions item={item} onOpenPanel={onOpenPanel} />;
        case 'monthly_narrative':
        case 'composer_draft':
        case 'coac_action_draft':
            return <ReviewLink label="Review draft" onClick={() => onOpenPanel(item)} />;
    }
}

// -------- task: inline approve / reject --------

function TaskActions({
    item,
    onOpenPanel,
}: {
    item: Extract<ReviewQueueItem, { kind: 'task' }>;
    onOpenPanel: (item: ReviewQueueItem) => void;
}) {
    const { reviewTask } = useAgent();
    const [rejecting, setRejecting] = useState(false);
    return (
        <>
            <div className="flex items-center gap-1.5">
                <ApproveButton onClick={() => void reviewTask(item.task.id, 'approve')} />
                <RejectButton onClick={() => setRejecting(true)} />
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onOpenPanel(item)}
                    className="ml-auto"
                >
                    Details
                    <ArrowRight className="w-3 h-3" />
                </Button>
            </div>
            <RejectionDialog
                open={rejecting}
                subject={`${item.primaryLabel} · ${item.secondaryLabel}`}
                onConfirm={(reason) => void reviewTask(item.task.id, 'reject', reason || undefined)}
                onClose={() => setRejecting(false)}
            />
        </>
    );
}

// -------- call_drafts: inline approve-all + review --------

function CallDraftsActions({
    item,
    onOpenPanel,
}: {
    item: CallDraftsReviewItem;
    onOpenPanel: (item: ReviewQueueItem) => void;
}) {
    const { pushToast } = useToast();
    const queryClient = useQueryClient();
    const [busy, setBusy] = useState(false);

    async function approveAll(): Promise<void> {
        setBusy(true);
        try {
            const tables: Array<[string, ReviewableKind]> = [
                ['call_extracted_facts', 'call-extracted-fact'],
                ['call_action_items', 'call-action-item'],
                ['call_kb_notes', 'call-kb-note'],
            ];
            const decisions: ReviewDecision[] = [];
            for (const [table, kind] of tables) {
                const res = await api.agent.listAgentRows({ params: { table }, query: {} });
                if (res.status !== 200) continue;
                const rows = res.body.rows as unknown as Array<{
                    id: string;
                    callId?: string;
                    reviewStatus?: string | null;
                }>;
                for (const r of rows) {
                    if (r.callId !== item.callId) continue;
                    if (r.reviewStatus != null && r.reviewStatus !== 'pending') continue;
                    decisions.push({ target: 'artefact', kind, id: r.id, decision: 'approve' });
                }
            }
            if (decisions.length === 0) {
                pushToast({ kind: 'info', message: 'Nothing left to approve' });
                return;
            }
            // One batch request commits every pending draft for this call.
            const batch = await submitReviewDecisions(decisions);
            if (!batch.ok) {
                pushToast({
                    kind: 'error',
                    message: batch.error ?? 'Could not approve all drafts',
                });
                return;
            }
            const { confirmed, failed } = batch.summary;
            pushToast(
                failed > 0
                    ? { kind: 'info', message: `Approved ${confirmed}, ${failed} failed` }
                    : {
                          kind: 'success',
                          message: `Approved ${confirmed} draft${confirmed === 1 ? '' : 's'}`,
                      },
            );
            void queryClient.invalidateQueries({ queryKey: ['review-queue'] });
            void queryClient.invalidateQueries({ queryKey: ['agent', 'rows'] });
        } catch {
            pushToast({ kind: 'error', message: 'Could not approve all drafts' });
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="flex items-center gap-1.5">
            <Button
                variant="primary"
                size="sm"
                busy={busy}
                onClick={() => void approveAll()}
                icon={<ThumbsUp className="w-3.5 h-3.5" />}
            >
                Approve all ({item.pendingCount})
            </Button>
            <ReviewLink label="Review" onClick={() => onOpenPanel(item)} className="ml-auto" />
        </div>
    );
}

// -------- shared buttons --------

function ApproveButton({ onClick }: { onClick: () => void }) {
    return (
        <Button
            variant="primary"
            size="sm"
            onClick={onClick}
            icon={<CheckCircle2 className="w-3.5 h-3.5" />}
        >
            Approve
        </Button>
    );
}

function RejectButton({ onClick }: { onClick: () => void }) {
    return (
        <Button
            variant="danger"
            size="sm"
            onClick={onClick}
            icon={<XCircle className="w-3.5 h-3.5" />}
        >
            Reject
        </Button>
    );
}

function ReviewLink({
    label,
    onClick,
    className,
}: {
    label: string;
    onClick: () => void;
    className?: string;
}) {
    return (
        <Button variant="ghost" size="sm" onClick={onClick} className={className}>
            {label}
            <ArrowRight className="w-3.5 h-3.5" />
        </Button>
    );
}
